import { useState } from 'react';
import type { ChartSpec } from '@/data/client';

/* One small chart beside an assistant answer, drawn as inline SVG like the
   sparklines on trafficIQ. Line for trends, bars for comparisons, at most
   three series in a fixed colour order, a legend whenever there is more than
   one, a hover tooltip on every mark, and a table view for anyone who wants
   the numbers. Text is never coloured by series; the mark carries identity. */

/* Validated as a categorical trio (lightness band, chroma, CVD separation,
   contrast on white): blue-700, amber-600 and the purple chart token. */
const SERIES_COLORS = ['var(--blue-700)', 'var(--amber-600)', 'var(--chart-3)'];
const W = 440, H = 200, PAD = { l: 44, r: 12, t: 12, b: 34 };

function fmt(v: number, unit?: ChartSpec['unit']): string {
  if (unit === 'usd') return v >= 1000 ? `$${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k` : `$${Math.round(v).toLocaleString('en-US')}`;
  if (unit === 'percent') return `${Math.round(v * 10) / 10}%`;
  if (unit === 'seconds') return `${Math.round(v)}s`;
  return v >= 1000 ? `${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k` : String(Math.round(v * 10) / 10);
}

function niceMax(max: number): number {
  if (max <= 0) return 1;
  const p = Math.pow(10, Math.floor(Math.log10(max)));
  const m = max / p;
  return (m <= 1 ? 1 : m <= 2 ? 2 : m <= 5 ? 5 : 10) * p;
}

export function AssistantChart({ spec }: { spec: ChartSpec }) {
  const [table, setTable] = useState(false);
  const [hover, setHover] = useState<{ i: number; s: number } | null>(null);
  const n = spec.labels.length;
  const max = niceMax(Math.max(...spec.series.flatMap((s) => s.values)));
  const innerW = W - PAD.l - PAD.r, innerH = H - PAD.t - PAD.b;
  const y = (v: number) => PAD.t + innerH - (v / max) * innerH;
  const ticks = [0, 0.5, 1].map((f) => f * max);
  const showEvery = Math.max(1, Math.ceil(n / 8));

  return (
    <figure className="asst-chart">
      <figcaption className="asst-chart__head">
        <span>{spec.title}</span>
        <button type="button" className="asst-chart__toggle" onClick={() => setTable((t) => !t)}>{table ? 'Chart' : 'Table'}</button>
      </figcaption>
      {table ? (
        <table className="dash-table asst-chart__table">
          <thead><tr><th></th>{spec.series.map((s) => <th key={s.name}>{s.name}</th>)}</tr></thead>
          <tbody>{spec.labels.map((l, i) => <tr key={l + i}><td>{l}</td>{spec.series.map((s) => <td key={s.name}>{fmt(s.values[i], spec.unit)}</td>)}</tr>)}</tbody>
        </table>
      ) : (
        <svg viewBox={`0 0 ${W} ${H}`} className="asst-chart__svg" role="img" aria-label={spec.title} onMouseLeave={() => setHover(null)}>
          {ticks.map((t) => (
            <g key={t}>
              <line x1={PAD.l} x2={W - PAD.r} y1={y(t)} y2={y(t)} className="asst-chart__grid" />
              <text x={PAD.l - 6} y={y(t) + 4} textAnchor="end" className="asst-chart__tick">{fmt(t, spec.unit)}</text>
            </g>
          ))}
          {spec.labels.map((l, i) => (i % showEvery === 0 || i === n - 1) && (
            <text key={l + i} x={PAD.l + (spec.type === 'bar' ? (i + 0.5) : n === 1 ? 0.5 : i / (n - 1)) * innerW} y={H - PAD.b + 16} textAnchor="middle" className="asst-chart__tick">{l.length > 10 ? `${l.slice(0, 9)}…` : l}</text>
          ))}
          {spec.type === 'bar' ? spec.series.map((s, si) => {
            const groupW = innerW / n; const gap = 2; const barW = Math.max(3, (groupW * 0.7 - gap * (spec.series.length - 1)) / spec.series.length);
            return s.values.map((v, i) => {
              const x = PAD.l + i * groupW + groupW * 0.15 + si * (barW + gap);
              const h = Math.max(0, y(0) - y(v));
              const on = hover && hover.i === i && hover.s === si;
              return (
                <g key={i} onMouseEnter={() => setHover({ i, s: si })}>
                  <rect x={x} y={y(v)} width={barW} height={h} rx={Math.min(4, barW / 2)} fill={SERIES_COLORS[si]} opacity={hover && !on ? 0.55 : 1} />
                  <rect x={x} y={y(0) - h} width={barW} height={h} fill="transparent" />
                  {h > 0 && <rect x={x} y={y(0) - 2} width={barW} height={2} fill={SERIES_COLORS[si]} />}
                </g>
              );
            });
          }) : spec.series.map((s, si) => {
            const pts = s.values.map((v, i) => [PAD.l + (n === 1 ? 0.5 : i / (n - 1)) * innerW, y(v)] as const);
            return (
              <g key={s.name}>
                <polyline points={pts.map((p) => p.join(',')).join(' ')} fill="none" stroke={SERIES_COLORS[si]} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
                {pts.map(([px, py], i) => (
                  <g key={i} onMouseEnter={() => setHover({ i, s: si })}>
                    <circle cx={px} cy={py} r={12} fill="transparent" />
                    <circle cx={px} cy={py} r={hover && hover.i === i ? 5 : 3.5} fill={SERIES_COLORS[si]} stroke="var(--white)" strokeWidth={2} />
                  </g>
                ))}
              </g>
            );
          })}
          {hover && (() => {
            const s = spec.series[hover.s]; const v = s.values[hover.i];
            const cx = PAD.l + (spec.type === 'bar' ? (hover.i + 0.5) : n === 1 ? 0.5 : hover.i / (n - 1)) * innerW;
            const tx = Math.min(W - PAD.r - 70, Math.max(PAD.l + 70, cx));
            return (
              <g className="asst-chart__tip" transform={`translate(${tx}, ${Math.max(PAD.t + 2, y(v) - 34)})`}>
                <rect x={-70} y={0} width={140} height={26} rx={6} />
                <text x={0} y={17} textAnchor="middle">{`${spec.labels[hover.i]} · ${spec.series.length > 1 ? `${s.name} ` : ''}${fmt(v, spec.unit)}`}</text>
              </g>
            );
          })()}
        </svg>
      )}
      {spec.series.length > 1 && (
        <ul className="asst-chart__legend">
          {spec.series.map((s, i) => <li key={s.name}><span className="asst-chart__swatch" style={{ background: SERIES_COLORS[i] }} />{s.name}</li>)}
        </ul>
      )}
    </figure>
  );
}
